import {
  GLOBAL_SEARCH_GROUP_LIMIT,
  GLOBAL_SEARCH_MIN_QUERY_LENGTH,
  GLOBAL_SEARCH_QUERY_KEY,
} from "@frontend-shared/constants";
import type { IGlobalSearchResponse } from "@frontend-shared/types";
import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import { globalSearchApi } from "@/api/services";
import { useAuth } from "@/hooks/use-auth";

interface IUseGlobalSearchProps {
  // NOTE: Already debounced by the caller.
  query: string;
  enabled?: boolean;
}

const GLOBAL_SEARCH_STALE_TIME_MS = 30_000;

// NOTE: NSAI-6589 — GET /search/global. The server answers with no groups below
// the minimum length, so the request is not sent at all there. The active
// project is part of the key: every source is scoped by X-Project-Uuid, and a
// cached answer from the project just left must never show in the new one.
export const useGlobalSearch = ({
  query,
  enabled = true,
}: IUseGlobalSearchProps): UseQueryResult<IGlobalSearchResponse> => {
  const { currentProjectUuid } = useAuth();
  const trimmedQuery = query.trim();

  return useQuery({
    queryKey: [GLOBAL_SEARCH_QUERY_KEY, currentProjectUuid, trimmedQuery],
    queryFn: () =>
      globalSearchApi.search({
        query: trimmedQuery,
        limit: GLOBAL_SEARCH_GROUP_LIMIT,
      }),
    enabled: enabled && trimmedQuery.length >= GLOBAL_SEARCH_MIN_QUERY_LENGTH,
    staleTime: GLOBAL_SEARCH_STALE_TIME_MS,
    retry: false,
  });
};
